import type { ShareSegment } from "../../components/ui/charts/ShareBar.js";
import type { StatusFilterOption } from "./StatusFilterChips.js";

/**
 * The shape of /employees/stats and the model the Summary strip draws from it.
 *
 * Lifted out of EmployeesPage so the dashboard's Employee report widget renders the
 * same chips and charts from the same numbers: one definition of what a status is
 * and what colour it wears, rather than two that drift apart.
 */

export interface StatRow { status: string; count: number }
export interface ChartRow { id: string | null; name: string; count: number }

export interface EmployeeStats {
  /** Headcount matching every active filter. */
  total: number;
  byStatus: StatRow[];
  byDept: ChartRow[];
  byOffice: ChartRow[];
}

// Status is a state, not a series, so it keeps the reserved status tokens rather
// than drawing from the categorical chart palette. One definition feeds both the
// filter chips and the Headcount mix bar.
export const STATUS_MIX = [
  { id: "ACTIVE",        label: "Active",        colorVar: "--color-success-fg", swatchClass: "bg-[var(--color-success-fg)]" },
  { id: "ONBOARDING",    label: "Onboarding",    colorVar: "--color-info-fg",    swatchClass: "bg-[var(--color-info-fg)]" },
  { id: "ON_LEAVE",      label: "On leave",      colorVar: "--color-accent-fg",  swatchClass: "bg-[var(--color-accent-fg)]" },
  { id: "NOTICE_PERIOD", label: "Notice period", colorVar: "--color-warning-fg", swatchClass: "bg-[var(--color-warning-fg)]" },
  { id: "TERMINATED",    label: "Terminated",    colorVar: "--color-error-fg",   swatchClass: "bg-[var(--color-error-fg)]" },
] as const;

export interface EmployeeSummaryModel {
  /** The charts' whole: the API's filtered total. */
  totalHeadcount: number;
  statusFilters: StatusFilterOption[];
  statusMix: ShareSegment[];
}

/**
 * byStatus is a facet count — every status is still counted when one of them is
 * selected — so the "All" chip sums those facets while the charts divide the
 * filtered total. Taking either number for the other makes the percentages lie.
 */
export function employeeSummaryModel(stats: EmployeeStats | null): EmployeeSummaryModel {
  const byStatus = stats?.byStatus ?? [];
  const statusMap = Object.fromEntries(byStatus.map((r) => [r.status, r.count]));

  return {
    totalHeadcount: stats?.total ?? 0,
    statusFilters: [
      { value: "", label: "All", count: byStatus.reduce((sum, r) => sum + r.count, 0), swatchClass: "" },
      ...STATUS_MIX.map((m) => ({ value: m.id, label: m.label, count: statusMap[m.id] ?? 0, swatchClass: m.swatchClass })),
    ],
    statusMix: STATUS_MIX.map((m) => ({ ...m, value: statusMap[m.id] ?? 0 })),
  };
}

/** The query string /employees/stats takes. Shared so the widget narrows exactly
 *  as the page does, and an added filter cannot reach one but not the other. */
export function employeeStatsQuery(filters: {
  search?: string;
  status?: string;
  departmentId?: string;
  officeId?: string;
  employeeType?: string;
}): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) if (value) params.set(key, value);
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}
